import { useState } from 'react';
import dayjs from 'dayjs';

import { usePurchaseDesk, useSupplierDesk, type SupplierDeskRow } from '@/api/purchaseDesk';
import { usePurchaseOrderGlance } from '@/api/purchaseOrders';
import { controlClass } from '@/components/Field';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { hueClasses, type Hue } from '@/components/hues';
import { formatAmount } from '@core/format/money';
import type { SupplierEventKind } from '@core/generated/enums';
import {
  deskNeeds,
  deskSteps,
  deskTotals,
  dueIsPast,
  dueWords,
  eventKinds,
  eventKindsLabel,
  eventLine,
  supplierFacts,
  type DeskStep,
} from '@core/purchasing/desk';
import { can, canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { BillScreen } from './BillScreen';
import { OrderComposer } from './OrderComposer';
import { PayScreen } from './PayScreen';
import { PurchaseBillsPage } from './PurchaseBillsPage';
import { PurchaseOrdersPage } from './PurchaseOrdersPage';
import { ReceiveScreen } from './ReceiveScreen';
import { VendorPaymentsPage } from './VendorPaymentsPage';

type Screen =
  | { kind: 'desk' }
  | { kind: 'order'; vendorId: number | null }
  | { kind: 'orders' }
  | { kind: 'openOrder'; id: number }
  | { kind: 'openBill'; id: number }
  | { kind: 'openPayment'; id: number }
  | { kind: 'receive'; orderId: number; vendorId: number }
  | { kind: 'bill'; vendorId: number }
  | { kind: 'pay'; vendorId: number }
  | { kind: 'split'; vendorId: number };

const rupees = (value: number) => `₹${formatAmount(value)}`;

/**
 * Shop › Purchases — the supplier desk (canvas "Purchase order flow", board 0, 2026-10-26; Option A of
 * the 2026-10-22 canvas before it). Four figures across suppliers, then who the shop buys from, each
 * with what is waiting: to come, to bill, to pay. Picking one shows where things stand with them —
 * ordered, received, not on a bill, to pay (core `deskSteps`) — the next move for each, and
 * everything lately done with them, filtered by kind.
 *
 * <p>
 * <strong>Nothing is worked out here.</strong> The desk is the server's reading of the orders,
 * deliveries, bills and payments (`GET /api/purchase-desk`); every paper still opens on its own page,
 * which this page reuses rather than copies.
 * </p>
 */
export function PurchasesPage({
  session,
  vendorId,
}: {
  session: Session;
  /** Opens with this supplier chosen — Outstanding's "Open on the supplier desk". */
  vendorId?: number | undefined;
}) {
  const [screen, setScreen] = useState<Screen>({ kind: 'desk' });
  const [selected, setSelected] = useState<number | null>(vendorId ?? null);
  const toDesk = () => setScreen({ kind: 'desk' });

  switch (screen.kind) {
    case 'order':
      return (
        <OrderComposer
          session={session}
          vendorId={screen.vendorId}
          onClose={toDesk}
          onSaved={(order) => {
            setSelected(order.supplierVendorId);
            setScreen({ kind: 'openOrder', id: order.id });
          }}
        />
      );
    case 'orders':
      return <PurchaseOrdersPage session={session} onExit={toDesk} />;
    case 'openOrder':
      return <PurchaseOrdersPage session={session} openId={screen.id} onExit={toDesk} />;
    case 'openBill':
      return <PurchaseBillsPage session={session} openId={screen.id} onExit={toDesk} />;
    case 'openPayment':
      return <VendorPaymentsPage session={session} openId={screen.id} onExit={toDesk} />;
    case 'split':
      return <VendorPaymentsPage session={session} payVendorId={screen.vendorId} onExit={toDesk} />;
    case 'receive':
      return (
        <ReceiveScreen
          session={session}
          orderId={screen.orderId}
          onClose={toDesk}
          onDone={toDesk}
        />
      );
    case 'bill':
      return (
        <BillScreen
          vendorId={screen.vendorId}
          onClose={toDesk}
          onSaved={(_, payNow) =>
            setScreen(payNow ? { kind: 'pay', vendorId: screen.vendorId } : { kind: 'desk' })
          }
        />
      );
    case 'pay':
      return (
        <PayScreen
          session={session}
          vendorId={screen.vendorId}
          onClose={toDesk}
          onDone={toDesk}
          onSplit={() => setScreen({ kind: 'split', vendorId: screen.vendorId })}
        />
      );
    case 'desk':
      return <Desk session={session} selected={selected} onSelect={setSelected} go={setScreen} />;
  }
}

/* ------------------------------------------------------------------------------------------------ */

function Desk({
  session,
  selected,
  onSelect,
  go,
}: {
  session: Session;
  selected: number | null;
  onSelect: (vendorId: number) => void;
  go: (screen: Screen) => void;
}) {
  const [all, setAll] = useState(false);
  const [find, setFind] = useState('');
  const branchId = session.branch?.id;
  const desk = usePurchaseDesk(branchId, all);
  const glance = usePurchaseOrderGlance(branchId);
  const rows = desk.data?.suppliers ?? [];
  const totals = deskTotals(rows);
  const needle = find.trim().toLowerCase();
  const shown = needle
    ? rows.filter(
        (row) => row.name.toLowerCase().includes(needle) || (row.mobile ?? '').includes(needle),
      )
    : rows;
  const chosen = selected ?? rows[0]?.vendorId ?? null;
  const today = dayjs().format('YYYY-MM-DD');

  return (
    <>
      <PageHeading
        eyebrow="Shop · Purchases"
        title="Purchases"
        subtitle="Who you buy from, and what is waiting with each — to come, to bill, to pay."
        actions={
          <>
            <Button icon="icon icon-list" onClick={() => go({ kind: 'orders' })}>
              All orders
            </Button>
            {canAct(session, 'PurchaseOrder:Create') && (
              <Button
                variant="primary"
                icon="icon icon-plus"
                shortcut="create"
                onClick={() => go({ kind: 'order', vendorId: chosen })}
              >
                New purchase order
              </Button>
            )}
          </>
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Figure
          label="Still to come"
          value={glance.data ? String(glance.data.stillToCome) : '—'}
          hint={
            glance.data
              ? `garments on ${plural(glance.data.openOrders, 'open order')}`
              : 'garments on open orders'
          }
          icon="icon icon-package"
          hue="primary"
        />
        <Figure
          label="Not on a bill"
          value={String(totals.toBill)}
          hint="received, waiting for their bill"
          icon="icon icon-file-text"
          hue="purple"
        />
        {totals.toPay !== null && (
          <Figure
            label="To pay"
            value={rupees(totals.toPay)}
            hint={totals.owedTo === 0 ? 'nothing owed' : `to ${plural(totals.owedTo, 'supplier')}`}
            icon="icon icon-wallet"
            hue="teal"
          />
        )}
        <Figure
          label="Late"
          value={glance.data ? String(glance.data.late) : '—'}
          hint="orders past their expected day"
          icon="icon icon-clock"
          hue="indigo"
          warn={(glance.data?.late ?? 0) > 0}
        />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 xl:col-span-4">
          <Card flush>
            <div className="border-border-color flex flex-col gap-2.5 border-b p-4">
              <label className="relative flex items-center">
                <i
                  className="icon icon-search text-default pointer-events-none absolute start-3"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  aria-label="Find a supplier"
                  placeholder="Find a supplier — name or mobile"
                  value={find}
                  onChange={(event) => setFind(event.target.value)}
                  className={`${controlClass} ps-9`}
                />
              </label>
              <div role="group" aria-label="Which suppliers" className="flex gap-1.5">
                {[
                  { value: false, label: all ? 'Needs action' : `Needs action · ${rows.length}` },
                  { value: true, label: 'All suppliers' },
                ].map((option) => (
                  <button
                    key={String(option.value)}
                    type="button"
                    aria-pressed={all === option.value}
                    onClick={() => setAll(option.value)}
                    className={`h-[30px] cursor-pointer rounded-full border px-3 text-[12px] ${
                      all === option.value
                        ? 'border-primary bg-primary font-semibold text-white'
                        : 'border-border-color bg-white text-gray-900 hover:bg-light'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {shown.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  icon="icon icon-truck"
                  title={
                    desk.isLoading
                      ? 'Loading…'
                      : needle
                        ? `No supplier matches “${find.trim()}”`
                        : 'Nothing is waiting'
                  }
                  hint="Suppliers show here while something is still to come, to bill or to pay."
                />
              </div>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-0.5 p-1.5">
                {shown.map((row) => (
                  <li key={row.vendorId} className="list-none">
                    <SupplierRow
                      row={row}
                      today={today}
                      active={row.vendorId === chosen}
                      onClick={() => onSelect(row.vendorId)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="col-span-12 xl:col-span-8">
          {chosen === null ? null : <SupplierPanel session={session} vendorId={chosen} go={go} />}
        </div>
      </div>
    </>
  );
}

/** A supplier's tile takes one of these, by its id, so it keeps its colour from day to day. */
const supplierHues: Hue[] = ['info', 'purple', 'teal', 'pink', 'indigo', 'cyan', 'orange'];

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter((word) => /^[\p{L}\p{N}]/u.test(word))
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('') || '?';

function SupplierTile({ vendorId, name, size }: { vendorId: number; name: string; size: string }) {
  const hue = hueClasses[supplierHues[vendorId % supplierHues.length]!];

  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-[10px] font-bold ${hue.wash} ${hue.ink} ${size}`}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

function SupplierRow({
  row,
  today,
  active,
  onClick,
}: {
  row: SupplierDeskRow;
  today: string;
  active: boolean;
  onClick: () => void;
}) {
  const due = dueWords(row, today);
  const late = dueIsPast(row, today);

  return (
    <button
      type="button"
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
      className={`flex w-full cursor-pointer items-center gap-3 rounded-[10px] border-s-[3px] py-2.5 ps-3 pe-2.5 text-start transition-colors ${
        active ? 'border-primary bg-primary-transparent' : 'hover:bg-light border-transparent'
      }`}
    >
      <SupplierTile vendorId={row.vendorId} name={row.name} size="size-[38px] text-[14px]" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-900">
          <span className="truncate">{row.name}</span>
          {row.isMsme && (
            <span className="bg-light rounded px-1.5 text-[10px] font-bold text-gray-700">
              MSME
            </span>
          )}
        </span>
        <span className="text-default text-[12px]">{deskNeeds(row)}</span>
        {due && (
          <span
            className={`text-[12px] ${late ? 'font-semibold text-[color:var(--color-warning)]' : 'text-default'}`}
          >
            {due}
          </span>
        )}
      </span>
      <i className="icon icon-chevron-right text-default" aria-hidden="true" />
    </button>
  );
}

/* ------------------------------------------------------------------------------------------------ */

/** Each step's ground and mark: green when done, amber while it is where things stand. */
const stepLook: Record<DeskStep['state'], { ground: string; dot: string }> = {
  done: { ground: 'bg-success-transparent', dot: 'bg-success text-white' },
  now: { ground: 'bg-warning-transparent', dot: 'bg-[color:var(--color-warning)] text-white' },
  later: { ground: 'bg-light', dot: 'bg-border-color text-gray-900' },
};

function SupplierPanel({
  session,
  vendorId,
  go,
}: {
  session: Session;
  vendorId: number;
  go: (screen: Screen) => void;
}) {
  const desk = useSupplierDesk(vendorId);
  const [kind, setKind] = useState<SupplierEventKind | null>(null);
  const today = dayjs().format('YYYY-MM-DD');

  if (!desk.data) {
    return null;
  }

  const { supplier, openOrderIds, events } = desk.data;
  const steps = deskSteps(desk.data, today);
  const first = supplier.name.split(' ')[0];
  const shown = kind === null ? events : events.filter((event) => event.kind === kind);
  const receive =
    supplier.toReceive > 0 && openOrderIds[0] !== undefined && canAct(session, 'ItemUnit:Create');
  const bill = supplier.toBill > 0 && canAct(session, 'PurchaseBill:Create');
  const pay =
    supplier.owed !== null && supplier.owed > 0 && canAct(session, 'VendorPayment:Create');

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3.5">
            <SupplierTile vendorId={vendorId} name={supplier.name} size="size-[52px] text-[18px]" />
            <div className="min-w-0">
              <h2 className="mb-0 font-serif text-[22px] font-semibold text-gray-900">
                {supplier.name}
              </h2>
              <p className="text-default mb-0 text-[13px]">{supplierFacts(supplier)}</p>
            </div>
          </div>
          {canAct(session, 'PurchaseOrder:Create') && (
            <Button icon="icon icon-plus" onClick={() => go({ kind: 'order', vendorId })}>
              Order from {first}
            </Button>
          )}
        </div>

        <ol
          aria-label={`Where things stand with ${supplier.name}`}
          className="m-0 mt-4 grid list-none grid-cols-1 gap-3 ps-0 sm:grid-cols-2 2xl:grid-cols-4"
        >
          {steps.map((step, index) => (
            <li
              key={step.key}
              className={`flex list-none items-start gap-3 rounded-xl p-3.5 ${stepLook[step.state].ground}`}
            >
              <span
                className={`flex size-8 shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${stepLook[step.state].dot}`}
                aria-hidden="true"
              >
                {step.state === 'done' ? <i className="icon icon-check" /> : index + 1}
              </span>
              <div className="min-w-0">
                <p className="mb-0 text-[13px] font-bold text-gray-900">{step.title}</p>
                <p className="font-mono-nums mb-0 text-xl font-bold text-gray-900">{step.figure}</p>
                <p
                  className={`mb-0 text-[12px] ${step.late ? 'font-semibold text-[color:var(--color-warning)]' : 'text-default'}`}
                >
                  {step.line}
                </p>
              </div>
            </li>
          ))}
        </ol>

        {(receive || bill || pay) && (
          <div className="border-border-color mt-4 flex flex-wrap items-center gap-2 border-t pt-4">
            <span className="me-1 text-[13px] font-semibold text-gray-900">Next</span>
            {receive && (
              <Button
                variant="primary"
                icon="icon icon-package-check"
                onClick={() => go({ kind: 'receive', orderId: openOrderIds[0]!, vendorId })}
              >
                Receive the {supplier.toReceive} still to come
              </Button>
            )}
            {bill && (
              <Button
                variant={receive ? 'secondary' : 'primary'}
                icon="icon icon-file-text"
                onClick={() => go({ kind: 'bill', vendorId })}
              >
                Enter their bill for {supplier.toBill}
              </Button>
            )}
            {pay && (
              <Button
                variant={receive || bill ? 'secondary' : 'primary'}
                icon="icon icon-wallet"
                onClick={() => go({ kind: 'pay', vendorId })}
              >
                Pay {rupees(supplier.owed!)}
              </Button>
            )}
          </div>
        )}
      </Card>

      <Card flush>
        <div className="border-border-color flex flex-wrap items-center justify-between gap-2.5 border-b px-[18px] py-4">
          <h3 className="mb-0 text-[15px] font-bold text-gray-900">Everything with {first}</h3>
          <div role="group" aria-label="Show" className="flex flex-wrap gap-1.5">
            {[null, ...eventKinds].map((each) => (
              <button
                key={each ?? 'all'}
                type="button"
                aria-pressed={kind === each}
                onClick={() => setKind(each)}
                className={`h-[30px] cursor-pointer rounded-full border px-3 text-[12px] ${
                  kind === each
                    ? 'border-primary bg-primary font-semibold text-white'
                    : 'border-border-color bg-white text-gray-900 hover:bg-light'
                }`}
              >
                {each === null ? 'All' : eventKindsLabel[each]}
              </button>
            ))}
          </div>
        </div>
        {shown.length === 0 ? (
          <p className="text-default mb-0 px-[18px] py-4 text-[13px]">Nothing yet.</p>
        ) : (
          <ul className="m-0 list-none px-[18px]">
            {shown.map((event) => {
              const line = eventLine(event);
              const look = eventLook[event.kind];
              const open: Screen | null =
                event.kind === 'Order'
                  ? { kind: 'openOrder', id: event.id }
                  : event.kind === 'Bill' && can(session, 'PurchaseBill:View')
                    ? { kind: 'openBill', id: event.id }
                    : event.kind === 'Payment' && can(session, 'VendorPayment:View')
                      ? { kind: 'openPayment', id: event.id }
                      : null;
              const owing = !event.cancelled && (event.pending ?? 0) > 0;

              return (
                <li
                  key={`${event.kind}-${event.id}`}
                  className="border-border-color flex list-none items-center gap-3 border-b py-3 last:border-b-0"
                >
                  <span
                    className={`flex size-[34px] shrink-0 items-center justify-center rounded-[9px] text-[11px] font-bold ${hueClasses[look.hue].wash} ${hueClasses[look.hue].ink}`}
                    aria-hidden="true"
                  >
                    {look.tag}
                  </span>
                  <div className="min-w-0 flex-1">
                    {open ? (
                      <button
                        type="button"
                        onClick={() => go(open)}
                        className="text-primary font-mono-nums cursor-pointer text-[13px] font-semibold hover:underline"
                      >
                        {line.title}
                      </button>
                    ) : (
                      <p className="font-mono-nums mb-0 text-[13px] font-semibold text-gray-900">
                        {line.title}
                      </p>
                    )}
                    <p className="text-default mb-0 text-[12px]">{line.detail}</p>
                  </div>
                  <span
                    className={`font-mono-nums text-end text-[13px] font-semibold whitespace-nowrap ${
                      owing ? 'text-[color:var(--color-warning)]' : 'text-gray-900'
                    }`}
                  >
                    {line.state}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}

/** Each kind of paper's tile in the history. */
const eventLook: Record<SupplierEventKind, { tag: string; hue: Hue }> = {
  Order: { tag: 'PO', hue: 'info' },
  Delivery: { tag: 'IN', hue: 'success' },
  Bill: { tag: 'BILL', hue: 'purple' },
  Payment: { tag: '₹', hue: 'teal' },
};

/** A figure across the desk: muted label, the figure, a hint, the hue's tile on the right. */
function Figure({
  label,
  value,
  hint,
  icon,
  hue,
  warn = false,
}: {
  label: string;
  value: string;
  hint: string;
  icon: string;
  hue: Hue;
  warn?: boolean;
}) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-default mb-1 truncate text-[length:var(--scale-list-name)]">{label}</p>
          <p
            className={`font-mono-nums mb-0 text-2xl font-bold max-lg:text-xl ${
              warn ? 'text-[color:var(--color-warning)]' : 'text-gray-900'
            }`}
          >
            {value}
          </p>
          <p className="text-default mt-0.5 mb-0 truncate text-[length:var(--scale-list-meta)]">
            {hint}
          </p>
        </div>
        <span
          className={`flex size-10 shrink-0 items-center justify-center rounded-md ${hueClasses[hue].tile}`}
          aria-hidden="true"
        >
          <i className={`${icon} text-lg text-white`} />
        </span>
      </div>
    </Card>
  );
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;
